import React, { useState, useEffect, useRef, useCallback } from 'react';
import { colors } from '../../utils/colors';
import { API_URLS } from '../../Api/urls';
import { getToken } from '../../Api/auth';
import type { Teacher } from '../../utils/types';
import type { ComplaintFineRecord, RecordType, RecordStatus } from './fineComplaintShared';
import {
  FINE_REASON_OPTIONS,
  COMPLAINT_REASON_OPTIONS,
  RECORD_STATUS_OPTIONS,
  RECORD_STATUS_STYLES,
  STATUS_LABEL,
  labelForReason,
} from './fineComplaintShared';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  teacher: Teacher;
  onBack: () => void;
}

interface StudentResult {
  id: number;
  full_name: string;
  student_uid: string;
}

type ModalState =
  | { mode: 'add' }
  | { mode: 'edit'; item: ComplaintFineRecord }
  | null;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const toYMD = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const todayStr = () => toYMD(new Date());

const formatDate = (dateStr: string) =>
  new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });

// ─── Main Screen ─────────────────────────────────────────────────────────────

const FineComplaintScreen: React.FC<Props> = ({ teacher, onBack }) => {
  const [records,     setRecords]     = useState<ComplaintFineRecord[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [apiError,    setApiError]    = useState('');
  const [modal,       setModal]       = useState<ModalState>(null);
  const [refreshKey,  setRefreshKey]  = useState(0);
  const [search,      setSearch]      = useState('');
  const [filter,      setFilter]      = useState<'all' | RecordType>('all');
  const [editLoading, setEditLoading] = useState<number | null>(null);

  const isSupervisor   = teacher.role === 'supervisor';
  const isClassTeacher = teacher.role === 'class_teacher';

  const getListUrl = useCallback(() => {
    if (isSupervisor)   return API_URLS.GET_COMPLAINT_FINES_LIST();
    if (isClassTeacher) return API_URLS.GET_MY_CLASS_COMPLAINT_FINES();
    return API_URLS.GET_MY_RECORDS_COMPLAINT_FINES();
  }, [isSupervisor, isClassTeacher]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setApiError('');
    try {
      const token = getToken();
      const res = await fetch(getListUrl(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setApiError('Failed to load records.'); return; }
      const data = await res.json();
      const list: ComplaintFineRecord[] = Array.isArray(data)
        ? data
        : (data.results ?? data.records ?? data.data ?? []);
      setRecords(list);
    } catch {
      setApiError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [getListUrl]);

  useEffect(() => { fetchRecords(); }, [fetchRecords, refreshKey]);

  const handleEdit = async (id: number) => {
    setEditLoading(id);
    try {
      const token = getToken();
      const res = await fetch(API_URLS.GET_COMPLAINT_FINE_BY_ID(id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const item: ComplaintFineRecord = await res.json();
      setModal({ mode: 'edit', item });
    } finally {
      setEditLoading(null);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = records
    .filter(r => filter === 'all' || r.record_type === filter)
    .filter(r =>
      !q ||
      r.student.full_name.toLowerCase().includes(q) ||
      (r.student.student_uid && r.student.student_uid.toLowerCase().includes(q))
    );

  return (
    <div style={{
      height: '100dvh',
      background: '#FDFCFE',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(40px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{
        padding: '30px 20px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
        borderBottom: `1px solid ${colors.border}`,
        background: '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onBack}
            style={{
              width: 36, height: 36, borderRadius: '50%',
              border: `1.5px solid ${colors.border}`,
              background: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke={colors.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: colors.text, lineHeight: 1.2 }}>
              Fine &amp; Complaint
            </div>
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 1 }}>
              {isSupervisor
                ? 'All students'
                : isClassTeacher
                  ? 'Your class students'
                  : 'Your created records'}
            </div>
          </div>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' as any, paddingBottom: 96 }}>

        {/* ── Filter chips + Search ── */}
        <div style={{ padding: '16px 16px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['all', 'fine', 'complaint'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '6px 14px', borderRadius: 20,
                  background: filter === f ? colors.primary : colors.bg,
                  color: filter === f ? '#fff' : colors.textMuted,
                  fontWeight: filter === f ? 700 : 500,
                  fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                  border: `1.5px solid ${filter === f ? colors.primary : colors.border}`,
                  transition: 'all 0.15s',
                }}
              >
                {f === 'all' ? 'All' : f === 'fine' ? 'Fines' : 'Complaints'}
              </button>
            ))}
          </div>
          <div style={{ position: 'relative' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke={colors.textMuted} strokeWidth="2"
              style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by student name…"
              style={{
                width: '100%', padding: '9px 12px 9px 32px',
                border: `1.5px solid ${colors.border}`, borderRadius: 10,
                fontSize: 13, fontFamily: 'inherit', outline: 'none',
                color: colors.text, background: '#fff', boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {/* ── Records list ── */}
        <div style={{ padding: '12px 16px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>

          {apiError && (
            <div style={{
              padding: '12px 16px', background: colors.dangerBg, borderRadius: 10,
              color: colors.danger, fontSize: 13, fontWeight: 500,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span>{apiError}</span>
              <button
                onClick={fetchRecords}
                style={{
                  background: colors.danger, color: '#fff', border: 'none',
                  borderRadius: 6, padding: '4px 12px',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Retry
              </button>
            </div>
          )}

          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{
                background: '#fff', borderRadius: 14, padding: '16px',
                border: `1.5px solid ${colors.border}`,
                display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                <div style={{ height: 14, width: '60%', borderRadius: 6, background: '#F0F0F0' }} />
                <div style={{ height: 11, width: '35%', borderRadius: 6, background: '#F0F0F0' }} />
                <div style={{ height: 11, width: '80%', borderRadius: 6, background: '#F0F0F0' }} />
              </div>
            ))
          ) : filtered.length === 0 ? (
            <div style={{
              padding: '48px 20px', textAlign: 'center',
              color: colors.textMuted, fontSize: 13,
              border: `1.5px dashed ${colors.border}`, borderRadius: 14, background: '#FAFAF8',
            }}>
              {q ? `No records found for "${search}"` : 'No records yet. Tap Add to create one.'}
            </div>
          ) : filtered.map(record => {
            const st = RECORD_STATUS_STYLES[record.status] ?? { bg: '#F5F5F5', text: '#6B7280', border: '#9CA3AF40' };
            const isFine = record.record_type === 'fine';
            const reasonLabel = record.reason === 'other' && record.custom_reason
              ? record.custom_reason
              : labelForReason(record.reason);
            const isEditingThis = editLoading === record.id;

            return (
              <div
                key={record.id}
                style={{
                  background: '#fff', borderRadius: 14, padding: '14px 16px',
                  border: `1.5px solid ${colors.border}`,
                  animation: 'fadeIn 0.2s ease',
                }}
              >
                {/* Top: student + badges */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                    <div style={{
                      fontSize: 14, fontWeight: 700, color: colors.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {record.student.full_name}
                    </div>
                    {record.student.student_uid && (
                      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 1 }}>
                        ID: {record.student.student_uid}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                      background: isFine ? '#FFF9E6' : '#FFF1F2',
                      color: isFine ? '#B45309' : '#BE123C',
                      border: `1px solid ${isFine ? '#F59E0B40' : '#F4364840'}`,
                    }}>
                      {isFine ? 'Fine' : 'Complaint'}
                    </span>
                    <span style={{
                      padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                      background: st.bg, color: st.text, border: `1px solid ${st.border}`,
                    }}>
                      {STATUS_LABEL[record.status] ?? record.status}
                    </span>
                  </div>
                </div>

                {/* Reason */}
                <div style={{
                  fontSize: 12, color: colors.textMuted, marginBottom: 10,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {reasonLabel}
                </div>

                {/* Bottom: date + amount + creator + edit */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 11, color: colors.textLight, flexShrink: 0 }}>
                      {formatDate(record.date)}
                    </span>
                    {Number(record.amount) > 0 && (
                      <span style={{ fontSize: 13, fontWeight: 700, color: colors.text, flexShrink: 0 }}>
                        ₹{Number(record.amount).toLocaleString('en-IN')}
                      </span>
                    )}
                    {record.creator && (isSupervisor || isClassTeacher) && (
                      <span style={{
                        fontSize: 11, color: colors.textMuted,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        By {record.creator.name}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleEdit(record.id)}
                    disabled={isEditingThis}
                    style={{
                      width: 30, height: 30, borderRadius: 8,
                      border: `1.5px solid ${colors.border}`,
                      background: '#fff',
                      cursor: isEditingThis ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      opacity: isEditingThis ? 0.6 : 1,
                    }}
                  >
                    {isEditingThis ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                        stroke={colors.textMuted} strokeWidth="2"
                        style={{ animation: 'spin 1s linear infinite' }}>
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                    ) : (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                        stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        background: '#fff',
        borderTop: `1px solid ${colors.border}`,
        padding: '12px 20px 28px',
        zIndex: 50,
      }}>
        <button
          onClick={() => setModal({ mode: 'add' })}
          style={{
            width: '100%', padding: '14px',
            background: colors.primary, border: 'none', borderRadius: 12,
            color: '#fff', fontSize: 15, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Fine / Complaint
        </button>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modal && (
        <AddEditModal
          modal={modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); setRefreshKey(k => k + 1); }}
        />
      )}
    </div>
  );
};

// ─── Add / Edit Modal (bottom sheet) ─────────────────────────────────────────

interface AddEditModalProps {
  modal: NonNullable<ModalState>;
  onClose: () => void;
  onSaved: () => void;
}

const AddEditModal: React.FC<AddEditModalProps> = ({ modal, onClose, onSaved }) => {
  const isEdit   = modal.mode === 'edit';
  const editItem = modal.mode === 'edit' ? modal.item : null;

  const [recordType,   setRecordType]   = useState<RecordType>(editItem?.record_type ?? 'fine');
  const [amount,       setAmount]       = useState(editItem?.amount && Number(editItem.amount) > 0 ? String(editItem.amount) : '');
  const [date,         setDate]         = useState(editItem?.date ?? todayStr());
  const [reason,       setReason]       = useState(editItem?.reason ?? '');
  const [customReason, setCustomReason] = useState(editItem?.custom_reason ?? '');
  const [remarks,      setRemarks]      = useState(editItem?.remarks ?? '');
  const [status,       setStatus]       = useState<RecordStatus>(editItem?.status ?? 'open');

  const [studentSearch,  setStudentSearch]  = useState('');
  const [studentResults, setStudentResults] = useState<StudentResult[]>([]);
  const [studentLoading, setStudentLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentResult | null>(
    editItem
      ? { id: editItem.student.id, full_name: editItem.student.full_name, student_uid: editItem.student.student_uid }
      : null,
  );

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const studentDropRef = useRef<HTMLDivElement>(null);

  // Close student dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!studentDropRef.current?.contains(e.target as Node)) setStudentResults([]);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced student search
  useEffect(() => {
    if (!studentSearch.trim()) { setStudentResults([]); return; }
    setStudentLoading(true);
    const t = setTimeout(async () => {
      try {
        const token = getToken();
        const res = await fetch(API_URLS.GET_LIST_OF_STUDENT({ search: studentSearch.trim() }), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { setStudentResults([]); return; }
        const data = await res.json();
        const list: any[] = Array.isArray(data) ? data : (data.students ?? data.data ?? []);
        setStudentResults(list.map(s => ({
          id: s.id,
          full_name: s.full_name,
          student_uid: s.student_uid ?? '',
        })));
      } catch {
        setStudentResults([]);
      } finally {
        setStudentLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [studentSearch]);

  const handleSave = async () => {
    setError('');
    if (!isEdit && !selectedStudent) { setError('Please select a student.'); return; }
    if (!reason)                      { setError('Please select a reason.'); return; }
    if (reason === 'other' && !customReason.trim()) { setError('Please enter a custom reason.'); return; }
    if (!remarks.trim())              { setError('Please enter remarks.'); return; }

    setSaving(true);
    try {
      const token = getToken();
      const fd = new FormData();
      if (!isEdit) {
        fd.append('student_id', String(selectedStudent!.id));
        fd.append('record_type', recordType);
      } else {
        fd.append('record_type', editItem!.record_type);
        fd.append('status', status);
      }
      fd.append('reason', reason);
      fd.append('remarks', remarks.trim());
      fd.append('date', date);
      fd.append('amount', String(Number(amount) || 0));
      if (reason === 'other') fd.append('custom_reason', customReason.trim());

      const url = isEdit
        ? API_URLS.UPDATE_COMPLAINT_FINE(editItem!.id)
        : API_URLS.CREATE_COMPLAINT_FINE;

      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setError(e.message ?? e.detail ?? 'Failed to save. Please try again.');
        return;
      }
      onSaved();
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setSaving(false);
    }
  };

  const reasonOptions = recordType === 'fine' ? FINE_REASON_OPTIONS : COMPLAINT_REASON_OPTIONS;

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px',
    border: `1.5px solid ${colors.border}`, borderRadius: 8,
    fontSize: 14, fontFamily: 'inherit', outline: 'none',
    color: colors.text, background: '#FEFEFE', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 11, fontWeight: 600, color: colors.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    display: 'block', marginBottom: 4,
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: '20px 20px 0 0',
        width: '100%', maxWidth: 540,
        boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
        maxHeight: '92dvh', display: 'flex', flexDirection: 'column',
        animation: 'slideInUp 0.25s ease',
      }}>

        {/* Drag handle */}
        <div style={{
          width: 36, height: 4, borderRadius: 2,
          background: colors.border, margin: '12px auto 0',
          flexShrink: 0,
        }} />

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px 14px',
          borderBottom: `1px solid ${colors.border}`,
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: colors.text }}>
              {isEdit
                ? `Edit ${editItem!.record_type === 'fine' ? 'Fine' : 'Complaint'}`
                : 'Add Record'}
            </div>
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
              {isEdit ? 'Update the existing record' : 'Add a fine or complaint for a student'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: '50%',
              border: `1.5px solid ${colors.border}`,
              background: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke={colors.textMuted} strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '16px 20px',
          display: 'flex', flexDirection: 'column', gap: 14,
          overflowY: 'auto', flex: 1,
        }}>

          {/* Record type toggle — add mode only */}
          {!isEdit && (
            <div>
              <label style={labelStyle}>Record Type</label>
              <div style={{
                display: 'flex', background: colors.bg, borderRadius: 9,
                padding: 3, border: `1.5px solid ${colors.border}`, gap: 3,
              }}>
                {([
                  { value: 'fine',      label: 'Fine' },
                  { value: 'complaint', label: 'Complaint' },
                ] as { value: RecordType; label: string }[]).map(rt => (
                  <button
                    key={rt.value}
                    type="button"
                    onClick={() => { setRecordType(rt.value); setReason(''); setCustomReason(''); }}
                    style={{
                      flex: 1, padding: '9px', border: 'none', borderRadius: 7,
                      background: recordType === rt.value ? '#E2CDF0' : 'transparent',
                      color: recordType === rt.value ? '#6B21A8' : colors.textMuted,
                      fontWeight: recordType === rt.value ? 700 : 500,
                      fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
                      transition: 'background 0.15s, color 0.15s',
                    }}
                  >
                    {rt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Student picker */}
          <div>
            <label style={labelStyle}>Student</label>
            {selectedStudent ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px',
                background: colors.primaryBg,
                border: `1.5px solid ${colors.primaryLight}`,
                borderRadius: 8,
              }}>
                <div style={{
                  width: 34, height: 34, borderRadius: '50%',
                  background: `${colors.primary}18`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 14, fontWeight: 700, color: colors.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {selectedStudent.full_name}
                  </div>
                  {selectedStudent.student_uid && (
                    <div style={{ fontSize: 11, color: colors.textMuted }}>
                      ID: {selectedStudent.student_uid}
                    </div>
                  )}
                </div>
                {!isEdit && (
                  <button
                    onClick={() => setSelectedStudent(null)}
                    style={{
                      padding: '5px 10px', background: '#fff',
                      border: `1.5px solid ${colors.border}`, borderRadius: 6,
                      cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: 12, fontWeight: 600, color: colors.textMuted,
                      flexShrink: 0,
                    }}
                  >
                    Change
                  </button>
                )}
              </div>
            ) : (
              <div ref={studentDropRef} style={{ position: 'relative' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke={colors.textMuted} strokeWidth="2"
                  style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  autoFocus
                  value={studentSearch}
                  onChange={e => setStudentSearch(e.target.value)}
                  placeholder="Search by name or ID…"
                  style={{ ...inputStyle, paddingLeft: 30 }}
                />
                {(studentLoading || studentResults.length > 0 || studentSearch.trim()) && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                    background: '#FFF',
                    border: `1.5px solid ${colors.border}`, borderRadius: 10,
                    boxShadow: '0 8px 28px rgba(0,0,0,0.13)',
                    zIndex: 9999, maxHeight: 220, overflowY: 'auto',
                  }}>
                    {studentLoading ? (
                      <div style={{ padding: '14px 12px', textAlign: 'center', fontSize: 13, color: colors.textMuted }}>
                        Searching…
                      </div>
                    ) : studentResults.length > 0 ? (
                      studentResults.map(s => (
                        <div
                          key={s.id}
                          onMouseDown={e => { e.preventDefault(); setSelectedStudent(s); setStudentSearch(''); setStudentResults([]); }}
                          onTouchEnd={() => { setSelectedStudent(s); setStudentSearch(''); setStudentResults([]); }}
                          style={{ margin: '4px 6px', padding: '10px 12px', borderRadius: 7, cursor: 'pointer' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#F3F4F6'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>{s.full_name}</div>
                          {s.student_uid && (
                            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 1 }}>
                              ID: {s.student_uid}
                            </div>
                          )}
                        </div>
                      ))
                    ) : studentSearch.trim() ? (
                      <div style={{ padding: '14px 12px', textAlign: 'center', fontSize: 13, color: colors.textMuted }}>
                        No students found for "{studentSearch}"
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Amount + Date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Amount (₹)</label>
              <input
                type="number" min="0" step="1"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Date</label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                max={todayStr()}
                style={inputStyle}
              />
            </div>
          </div>

          {/* Reason */}
          <div>
            <label style={labelStyle}>Reason</label>
            <select
              value={reason}
              onChange={e => { setReason(e.target.value); if (e.target.value !== 'other') setCustomReason(''); }}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">Select reason…</option>
              {reasonOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Custom reason — shown when "other" selected */}
          {reason === 'other' && (
            <div>
              <label style={labelStyle}>Custom Reason</label>
              <textarea
                value={customReason}
                onChange={e => setCustomReason(e.target.value)}
                placeholder="Enter custom reason…"
                rows={2}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 56 }}
              />
            </div>
          )}

          {/* Remarks */}
          <div>
            <label style={labelStyle}>Remarks</label>
            <textarea
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              placeholder="Enter remarks…"
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
            />
          </div>

          {/* Status — edit mode only */}
          {isEdit && (
            <div>
              <label style={labelStyle}>Status</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value as RecordStatus)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                {RECORD_STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Validation / API error */}
          {error && (
            <div style={{
              padding: '10px 14px',
              background: colors.dangerBg,
              border: `1.5px solid ${colors.danger}30`,
              borderRadius: 8, color: colors.danger, fontSize: 13, fontWeight: 500,
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 10,
          padding: '14px 20px 28px',
          borderTop: `1px solid ${colors.border}`,
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '12px',
              border: `1.5px solid ${colors.border}`, borderRadius: 10,
              background: '#fff', fontSize: 14, fontWeight: 600,
              cursor: 'pointer', color: colors.textMuted, fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1, padding: '12px', border: 'none', borderRadius: 10,
              background: saving ? colors.primaryLight : colors.primary,
              fontSize: 14, fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              color: '#fff', fontFamily: 'inherit',
              transition: 'background 0.15s',
            }}
          >
            {saving ? 'Saving…' : isEdit ? 'Update' : 'Save Record'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FineComplaintScreen;
